import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dynamicConfigApi } from "../../../modules/dynamicconfig/api";
import {
  CreateUpdateDynamicConfigProperty,
  DynamicConfigPropertyFilterPageRequest,
} from "../../../modules/dynamicconfig/types";
import { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const DYNAMIC_CONFIG_QUERY_KEYS = {
  filterConfigProperties: (payload?: DynamicConfigPropertyFilterPageRequest) =>
    payload ? ["filterConfigProperties", payload] : ["filterConfigProperties"],
  createConfigProperty: () => ["createConfigProperty"],
  updateConfigProperty: () => ["updateConfigProperty"],
  deleteConfigProperty: () => ["deleteConfigProperty"],
};

export const useFilterConfigProperties = (payload: DynamicConfigPropertyFilterPageRequest) => {
  return useQuery({
    queryKey: DYNAMIC_CONFIG_QUERY_KEYS.filterConfigProperties(payload),
    queryFn: async () => {
      const { data } = await dynamicConfigApi.filterConfigProperties(payload);

      return data;
    },
  });
};

export const useCreateConfigProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DYNAMIC_CONFIG_QUERY_KEYS.createConfigProperty(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateDynamicConfigProperty) => {
      const { data } = await dynamicConfigApi.createConfigProperty(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DYNAMIC_CONFIG_QUERY_KEYS.filterConfigProperties() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateConfigProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DYNAMIC_CONFIG_QUERY_KEYS.updateConfigProperty(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateDynamicConfigProperty>) => {
      const { data } = await dynamicConfigApi.updateConfigProperty(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DYNAMIC_CONFIG_QUERY_KEYS.filterConfigProperties() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteConfigProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DYNAMIC_CONFIG_QUERY_KEYS.deleteConfigProperty(),
    mutationFn: (id: UUID) => {
      return dynamicConfigApi.deleteConfigProperty({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DYNAMIC_CONFIG_QUERY_KEYS.filterConfigProperties() });
      Notification.itemDeletedNotification();
    },
  });
};
